/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, Observable } from 'rxjs';
import { first, tap } from 'rxjs/operators';
import { Field } from '@src/app/shared/field/Field';
import { ServiceRequest } from '@src/app/model/serviceRequest';

import { User } from '@src/app/model/user';
import { ChatComponent } from '@src/app/shared/chat/chat-component/chat.component';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { AttachmentGridComponent } from '@src/app/shared/form/file/attachment-grid/attachment-grid.component';
import { ActionHolder, IFormAction } from '@src/app/shared/form/form.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { UserService } from '@src/app/modules/user/user.service';
import { ActionService } from '@src/app/modules/crm/action.service';
import { AiProposalResult, RequestService } from '@src/app/modules/crm/request.service';

import { ChatField } from '@src/app/shared/chat/chat-field';
import { ActionGridComponent } from '@src/app/shared/action/action-grid.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { ServiceRequestPageNavRoute } from '@src/app/routes/service-request-page-nav';
import { RequestTemplatesService } from '@src/app/modules/crm/request-templates.service';
import { TrendField } from '@src/app/shared/field/TrendField';
import { MessageService } from '@src/app/shared/message.service';
import { NavOption } from '@src/app/model/sys/navOption';
import { Cycle } from '@src/app/model/cycle';
import { AcceptRequestAction, AddActionFormAction, AddMemoAction, ReOpenRequestAction, ResolveRequestAction } from '@src/app/modules/crm/service-request-page/resolve-request/ResolveRequestAction';
import { UnitDetailsAction } from "@src/app/modules/crm/service-request-page/UnitDetailsAction";
import { PersonDetailsAction } from "@src/app/modules/crm/service-request-page/PersonDetailsAction";
// import { AddTemplateAction } from "@src/app/modules/crm/service-request-page/AddTemplateAction";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AiLog } from '@src/app/model/ai-log';
import { AiProposalComponent, AiProposalData } from '@src/app/modules/crm/service-request-page/ai-proposal/ai-proposal.component';
import { AbstractObject, uuid } from '@src/app/model/abstract-object';
import { GridField } from '@src/app/shared/grid/grid-field';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { CurrencyPipe, NgTemplateOutlet } from '@angular/common';
import { RequestApprovalTileComponent } from '@src/app/modules/crm/service-request-page/request-approval/request-approval-tile.component';
import { RequestAgendaDialogComponent } from '@src/app/modules/crm/service-request-page/request-approval/request-agenda-dialog.component';
import { DialogOptions } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { ImageUploadRequestAction } from '@src/app/modules/crm/service-request-page/image-upload-request/image-upload-request.component';
import { IsNarrowService } from '@src/app/shared/is-narrow.service';
import { MemoField } from "@src/app/shared/memos/MemoField";
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { CtlHolderComponent } from '@src/app/shared/form/ctl-holder/ctl-holder.component';
import { TextEditComponent } from '@src/app/shared/simpleFields/text-edit/text-edit.component';
import { SectionActionButtonComponent } from '@src/app/shared/section-action-button/section-action-button.component';
import { RequestWorkOrderEditor } from '@src/app/modules/crm/service-request-page/request-work-order-editor';
import { RelatedRequestsComponent } from '@src/app/shared/related-requests/related-requests.component';
import { NewRequestData, RequestContextService } from '@src/app/modules/crm/request-context.service';
import { RequestTemplatePageNavRoute } from '@src/app/routes/request-template-page-nav';
import { NewRequestType } from '@src/app/modules/crm/new-request-stepper/new-request-stepper.component';
import { SendSupplierAction } from '@src/app/modules/crm/service-request-page/send-supplier/send-supplier-action';
import { Router, RouterLink } from '@angular/router';
import { Txn } from '@src/app/model/txn';
import { PurchasePageNavRoute } from '@src/app/routes/purchase-page-nav';
import { MyInjector } from '@src/app/app.module';
import { MailService } from '@src/app/shared/mail-service';
import { SendEmailFormAction } from '@src/app/shared/action/send-email-form-action';
import { AddFollowerAction } from '@src/app/shared/chat/add-follower';
import { FormDateTimeComponent } from '@src/app/shared/form/form-date-time/form-date-time.component';
import { PillField } from '@src/app/shared/form/form-pill/form-pill.component';
import { makeRequestChannelPillField } from '@src/app/modules/social/channelPillFields';
import { SectionEditor } from '@src/app/shared/form/section-editor';
import { RequestSummaryEditor } from '@src/app/modules/crm/service-request-page/request-summary-editor';
import { RequestDetailsEditor } from '@src/app/modules/crm/service-request-page/request-details-editor';
import { RequestPeopleEditor } from '@src/app/modules/crm/service-request-page/request-people-editor';
import {
    aiStatusOf, makeActionStatusPillField, makeAssistantPillField, makeStatusPillField
} from '@src/app/modules/crm/service-request-page/requestPillFields';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { PersonalRequestPageNavRoute } from '@src/app/routes/app-router';
import { AddCommentAction } from '@src/app/shared/chat/add-comment';
import { RequestCategory } from '@src/app/model/RequestCategory';
import { RequestCategoriesService } from '@src/app/modules/crm/request-categories.service';
import { SrTopic } from '@src/app/model/sr-topic';
import { SrClass } from '@src/app/model/sr-class';
import { SrTopicService } from '@src/app/modules/crm/sr-topic.service';
import { SrClassService } from '@src/app/modules/crm/sr-class.service';
import { Role } from '@src/app/model/role';

@Component({
    selector: 'app-service-request-page',
    templateUrl: './service-request-page.component.html',
    styleUrls: ['./service-request-page.component.scss'],
    imports: [FormPageComponent, CtlHolderComponent, TextEditComponent, NgTemplateOutlet,
        SectionActionButtonComponent, CurrencyPipe, RouterLink, RequestApprovalTileComponent]
})
export class ServiceRequestPageComponent extends AbstractPageComponent {
    /*
     * The page body is this component's own template (see the .html), not the standard field-set
     * layout: config.fieldSet.formLayout is left empty so app-field-set draws nothing, while every
     * field still lives on the config so the list view, filters, views and the docs extract are
     * untouched.
     */
    dataSvc = inject(RequestService);
    actionSvc = inject(ActionService);
    private tmplService = inject(RequestTemplatesService);
    private msgSvc = inject(MessageService);
    private dialog = inject(MatDialog);
    private destroyRef = inject(DestroyRef);
    private isNarrowSvc = inject(IsNarrowService);
    private mailSvc = inject(MailService);
    private reqContextSvc = inject(RequestContextService);
    // Opened as a dialog by the recurring jobs page - see its lastRequest link.
    dialogRef = inject<MatDialogRef<ServiceRequestPageComponent>>(MatDialogRef, { optional: true });
    dialogOptions = inject<DialogOptions>(MAT_DIALOG_DATA, { optional: true });


    /*
     * The list is a mix of both request types, so it keeps the generic title; the detail view names
     * the record it is showing - "Case" or "Work Order" - see setPageTitle / beforeList.
     */
    private static readonly listTitle = $localize`Service Request`;

    actions: IFormAction[] = [];

    users: User[] = [];
    cats: RequestCategory[] = [];

    isPhone = this.isNarrowSvc.isPhone();

    teamField = FormPicklistComponent.make('OMC', 'teamId', 'teamName',
        { selfPopulate: true }, { readonly: true, disable: true, condensed: true, visible: Field.noForm });
    agentField = FormPicklistComponent.make('Agency', 'agentTeamId', 'agentName',
        { selfPopulate: true }, { readonly: true, disable: true, condensed: true, visible: Field.noShow });

    prioField = ServiceRequest.getPriorityPillField();

    srClasses: SrClass[] = [];

    /** The request we have already put to the assistant on this visit, and what came back. */
    private askedAbout: uuid = null;
    private aiResult: AiProposalResult = null;
    private topicClass = ServiceRequest.makeTopicAndClassFields(() => this.srClasses);
    srTopicField = this.topicClass.topicField;
    srClassField = this.topicClass.classField;

    attachmentGrid = AttachmentGridComponent.make(this.dataSvc, { formRow: 2 }).showSimpleVersion({});


    /*
     * Status / Action Status - coloured pills, not editable fields. One 'pill' field each serves
     * both the Details tile (condensed row, via <app-ctl-holder>) and the requests table column
     * (table-cell @case 'pill'). Click flows and colours live in requestPillFields.ts.
     */
    statusPillField = makeStatusPillField();
    actionStatusPillField = makeActionStatusPillField({ afterFormChange: () => this.reloadForm() });
    aiField = makeAssistantPillField({
        review: (sr) => this.reviewProposal(sr),
        afterFormChange: () => this.reloadForm(),
    });

    /** The request currently on the form - Status / Action Status pills and the edit dialogs read it */
    get sr(): ServiceRequest {
        return this.page?.form?.focusItem as ServiceRequest;
    }

    /** The Work Order rail section is hidden on a Case - see the template. */
    /** The invoices and accruals raised against this work order - see the Actual list on the tile. */
    txnTypeName(txn: Txn) {
        return Txn.getType(txn.typeId)?.name ?? '';
    }

    /** A purchase or accrual credits the creditor, a credit note debits it - so this signs the cost. */
    txnAmount(txn: Txn) {
        return (txn.credit ?? 0) - (txn.debit ?? 0);
    }

    txnLink(txn: Txn) {
        return PurchasePageNavRoute.getIdUrl(txn.id, txn.teamId);
    }

    get isWorkOrder(): boolean {
        return this.sr?.requestType === ServiceRequest.typeWorkOrder.id;
    }

    /*
     * Held as members so the two rails in the template can place each field by reference rather
     * than by name. Left rail: Details + Approval. Right rail: Dates, People and Work Order.
     */
    refField = ServiceRequest.getRefField().override({ visible: Field.formOnly, formColumn: 1 });
    titleField = ServiceRequest.getTitleField().cellStyle('min-width: 30em');
    descriptionField = ServiceRequest.getDescriptionField();
    sourceField = ServiceRequest.getSourceField();
    requestTypeField = ServiceRequest.getRequestTypeField();
    reportedByField = ServiceRequest.getReportedByField().override({ condensed: true });
    billableField = ServiceRequest.getBillableField().override({ visible: Field.formOnly });
    createdField = FormDateTimeComponent.make('Created', 'createdAt',
        { readonly: true, condensed: true, formColumn: 4 });
    completedField = ServiceRequest.getCompletedField();
    resolvedByField = ServiceRequest.getResolvedByField();
    ownerReadField = ServiceRequest.getOwnerReadAtField().override({ visible: Field.formOnly });
    lastUpdateField = FormDateTimeComponent.make('Last Updated', 'updatedAt',
        { readonly: true, disable: true, formColumn: 4, visible: Field.formOnly, condensed: true });
    forumField!: PillField;

    actionGrid = ActionGridComponent.make('Request', {
        formRow: 2, actionCompleteFn: this.reloadForm.bind(this)
    }).showSimpleVersion().hide();

    dataGrid = new GridField({
        field: { label: 'Data', value: 'dataFields', sendServer: false, tabIcon: 'tune' },
        rowFactory: () => [
            FormTextComponent.make('Name', 'name', { readonly: true, cellOpts: { style: 'text-align: right' } }),
            FormTextComponent.make('Value', 'value', { readonly: true, cellOpts: { width: '80%' } }),
        ]
    }).hide();

    /*
     * Replaced by the Status pill's click (see requestPillFields.openFullRequest). Kept
     * commented, not deleted, until the pill flow is fully tested.
     *
    closeButton = FormButtonComponent.makeTableButton(' ', 'closeButton', (sr: ServiceRequest) => {
        if (sr.subStatus === ServiceRequest.srUnplanned.id) {
            const dialogRef = this.dialog.open(ResolveRequestComponent, {data: sr});
            dialogRef.afterClosed().subscribe( srUpdated => {
                if (srUpdated) {
                    Object.assign(sr, srUpdated);
                }
            });
        } else {
            if (sr.srStatus !== ServiceRequest.srOpen.id) {
                this.msgSvc.warn('Request is already closed');
            } else {
                this.msgSvc.warn('Can only close requests that are open, and do not have outstanding actions');
            }
        }
    }, {
        calculateValue: (o: ServiceRequest) => {
                if (o.srStatus === ServiceRequest.srOpen.id) {
                    if (o.subStatus === ServiceRequest.srUnplanned.id) {
                        return 'check_box_outline_blank'
                    }
                    return ''
                } else if (o.srStatus === ServiceRequest.srResolved.id) {
                    return 'check';
                } else {
                    return 'done_all';
                }
            },
            visible: Field.noForm, type: 'icon',
            tableIconStyler: (sr: ServiceRequest) => {
                if (sr.subStatus === ServiceRequest.srUnplanned.id) {
                    return {'color': 'green'};
                } else if (sr.subStatus === ServiceRequest.srOverdue.id) {
                    return {'color': 'red'};
                } else if (sr.srStatus !== ServiceRequest.srOpen.id) {
                    return { 'color': 'black' };
                }
                return {'color': 'green'};
            },
            toolTip: $localize`Close this request`
    }).cellStyle('padding-left: 0px; padding-right: 0px');
    */

    approvedByField = ServiceRequest.getApprovedByField();
    approvedAtField = ServiceRequest.getApprovedAtField();

    /*
     * A request is normally owned by the agent, but can be handed to an OMC member - a director,
     * say. Both audiences come back as one list from /requestOwners, so reassigning is a single
     * pick. Shared with the "Edit the people" dialog - see ServiceRequest.makeOwnedByField.
     */
    ownedByField = ServiceRequest.makeOwnedByField().override({ formColumn: 3 });

    /*
     * Replaced by the Status pill's click (see requestPillFields.openFullRequest, which opens
     * the whole request in a dialog for anything other than an open+unplanned request). Kept
     * commented, not deleted, until the pill flow is fully tested.
     *
    editButton = FormButtonComponent.makeTableButton(' ', 'editButton', (sr: ServiceRequest) => {
        const dialogRef = this.dialog.open(ServiceRequestPageComponent, { data: {id: sr.id }});
        dialogRef.afterClosed().subscribe(srUpdated => {
            if (srUpdated) {
                Object.assign(sr, srUpdated);
            } else {
                this.dataSvc.getOne(sr.id, null).subscribe(srUpdated => {
                    if (srUpdated) {
                        Object.assign(sr, srUpdated);
                    }
                });
            }
        });
    }, {
        calculateValue: () => 'open_in_new',
        visible: Field.noForm, type: 'icon',
        tableIconStyler: (sr: ServiceRequest) => {
            if (sr.subStatus === ServiceRequest.srUnplanned.id) {
                return {'color': 'orange'};
            } else if (sr.subStatus === ServiceRequest.srOverdue.id) {
                return {'color': 'red'};
            }
            return {'color': 'green'};
        },
        toolTip: $localize`Edit Request`
    }).cellStyle('padding-left: 0px; padding-right: 0px');
    */

    drilldownButton = FormNavLinkComponent.makeDetail('Ref',
        (o:ServiceRequest) => o.refNr, 'id', ServiceRequestPageNavRoute,
    ).cellStyle('padding-right:0px; width: 6em; text-align: end').override({visible: Field.noForm})



    parentField = ServiceRequest.makeParentField(this.dataSvc).override({ formColumn: 1, visible: Field.formOnly });

    // Child requests - the filter-chip list + expand + add-child, all in the shared component.
    childRequestsField = RelatedRequestsComponent.make({
        label: 'Child Requests',
        value: 'childRequests',
        navRoute: ServiceRequestPageNavRoute,
        parentRequest: () => this.page.form.focusItem as ServiceRequest,
        changed: () => this.reloadForm(),
    }).showSimpleVersion();

    chatField: ChatField = ChatComponent.make(this.dataSvc, { formRow: 2 }, false

    ).hide().allowComments().promptAfterSaveComment(() => this.promptForWaiting()).refreshAfterComment(this.reloadForm.bind(this));

    weekField = TrendField.make('Week Created', 'weekCreated', { cellOpts: { heading: 'Week' } });

    plannedField = FormPicklistComponent.make('Planned', 'planned', '',
        { items: [{ id: 0, name: 'No' }, { id: 1, name: 'Yes' }] }).hide();

    openField = FormPicklistComponent.make('Open', 'open', '',
        { items: [{ id: 0, name: 'No' }, { id: 1, name: 'Yes' }] }
    ).hide();

    propertyManagerField = FormPicklistComponent.make('Property Manager', 'omc.managerId', null,
        { items: this.users },
        {
            formColumn: 4, readonly: true, sendServer: false, disable: true, condensed: true,
            calculateValue: o => (o as ServiceRequest).omc?.managerId,
            visible: Field.formOnly,
        }
    ).hide();

    templateField = FormPicklistComponent.make('Standard Request', 'templateId', null,
        { service: this.tmplService },
        { formColumn: 1, readonly: true, sendServer: false, disable: true }
    ).hide();

    // The supplier of the work - shared with the "Edit the work order" dialog. See
    // ServiceRequest.makeSupplierField.
    supplierField = ServiceRequest.makeSupplierField();

    // Unit + Contact travel together (unit change clears the contact) - see
    // ServiceRequest.makeUnitAndContactFields. Shared with the "Edit the people" dialog.
    private unitContact = ServiceRequest.makeUnitAndContactFields();
    unitField = this.unitContact.unitField.override({ formColumn: 2 });
    personField = this.unitContact.personField.override({ formColumn: 2 });

    // Where in the development the request is - shown on the People tile, edited in its dialog.
    // Read only here: selfPopulate builds the one option it needs from the joined areaName /
    // coreName rather than fetching that OMC's whole list to show one name.
    areaField = FormPicklistComponent.make($localize`Area`, 'areaId', 'areaName',
        { selfPopulate: true },
        { readonly: true, disable: true, formColumn: 2, visible: Field.formOnly });
    coreField = FormPicklistComponent.make($localize`Core`, 'coreId', 'coreName',
        { selfPopulate: true },
        { readonly: true, disable: true, formColumn: 2, visible: Field.formOnly });

    /* All three open the stepper - there is no full-screen new request form any more. */
    newCaseItem = { name: $localize`New Case`, id: 'newCase', icon: ServiceRequestPageNavRoute.icon,
        action: () => this.newRequest('case') } as NavOption

    /* The same stepper, with the standard request to build from as its first step. */
    newTemplatedItem = { name: $localize`New Standard Request`, id: 'newTemplatedRequest',
        icon: RequestTemplatePageNavRoute.icon,
        action: () => this.newRequest('case', { templated: true }) } as NavOption

    newWorkOrderItem = { name: $localize`New Work Order`, id: 'newWorkOrder', icon: 'construction',
        action: () => this.newRequest('workOrder') } as NavOption

    memoField = new MemoField(this.dataSvc);

    openMonthPL = FormPicklistComponent.make('Open Month', 'monthOpened', 'openMonth',
        { items: [], selfPopulate: true }, {
        visible: Field.noShow, calculateValue: (o: ServiceRequest) => o.openMonth
    });

    mailItemGrid = this.mailSvc.getMailItemGrid(this.dataSvc, 'model');

    tabFields = [
        this.dataGrid,
        this.mailItemGrid,
    ]

    config = new FormConfig({
        navRoute: ServiceRequestPageNavRoute,
        docPath: 'omc-professional/requests',
        title: ServiceRequestPageComponent.listTitle,
        help: $localize`Service Requests to get things done`,
        fieldSet: new FieldSet({
            // Attachments / actions / comments are shown inline in the page body, not as tabs.
            // Adding them to the fieldSet still gets them initialised and fed a value like any
            // other field; formLayout is empty so app-field-set draws nothing.
            fields: [...this.getFields(), this.attachmentGrid, this.actionGrid, this.chatField, this.childRequestsField],
            formLayout: []
        }),
        service: this.dataSvc,
        configReady: this.configReady,
        actions: this.actions,
        beforeEdit: this.beforeEdit.bind(this),
        beforeList: this.beforeList.bind(this),
        newOptions: [
            this.newCaseItem,
            this.newWorkOrderItem,
            this.newTemplatedItem
        ],
        tabFields: this.tabFields,
        tableHighlighter: (o) => (o as ServiceRequest).ownerReadAt ? false : true,
        allowNew: this.dialogRef ? false : true,
        hideFormActions: true,
        fixedFormHeight: false
    });

    /**
     * A request raised from the menu has no parent to take its tenancy from, so newRequest() asks
     * which OMC before the stepper opens. Cancelling that opens nothing; the list stays where it is.
     */
    newRequest(requestType: NewRequestType, extra: NewRequestData = {}) {
        this.reqContextSvc.newRequest({ requestType, ...extra }).subscribe(created => {
            if (created) {
                MyInjector.instance.get(Router).navigate([ServiceRequestPageNavRoute.getIdUrl(created.id)]);
            }
        });
    }

    currentCycle: Cycle;

    constructor() {
        const teamUserSvc = inject(UserService);
        const catSvc = inject(RequestCategoriesService);
        const srTopicSvc = inject(SrTopicService);
        const srClassSvc = inject(SrClassService);
        const currentUserSvc = inject(CurrentUserService);

        super();

        this.isNarrowSvc.detectVeryNarrow().pipe(takeUntilDestroyed()).subscribe(result => { this.isPhone = result; });

        // The rails show their fields as compact label/value rows. Visibility in the requests
        // list/table is left to each field's own definition.
        this.railFields().forEach(field => {
            field.condensed = true;
        });

        forkJoin({
            currentUser: currentUserSvc.getCurrentUser().pipe(first()),
            users: teamUserSvc.getAgentUsers(),
            cats: catSvc.get<RequestCategory>(false),
            srTopics: srTopicSvc.get<SrTopic>(false),
            srClasses: srClassSvc.get<SrClass>(false)
        }).subscribe((result) => {
            if (this.redirectedToOwnRequests(result.currentUser)) {
                return;
            }
            this.currentUser = result.currentUser;
            this.users.push(...result.users);
            this.cats = result.cats;
            this.srClasses = result.srClasses;
            this.srTopicField.setPicklistItems(result.srTopics);
            this.srClassField.setPicklistItems(result.srClasses);

            this.setupForAudience();
            this.actionGrid.users = this.users;
            this.actionGrid.currentUser = this.currentUser;
            this.configReady.next(null);
        });
    }

    /**
     * Emails link owners and directors here as well as agents, but an owner has no privilege on the
     * page - send them to the version of the request they are allowed to see.
     */
    private redirectedToOwnRequests(currentUser: User): boolean {
        if (currentUser.inAgency) {
            return false;
        }
        if (User.getModelPrivilege(currentUser, 'ServiceRequest').getIndex) {
            return false;
        }
        const url = window.location.pathname;
        const id = url.substring(url.lastIndexOf('/') + 1);
        MyInjector.instance.get(Router).navigate([PersonalRequestPageNavRoute.getIdUrl(id)]);
        return true;
    }

    /** The property manager column and the internal memo tab are for agency users only */
    private setupForAudience() {
        if (this.currentUser.inAgency && !this.isPhone) {
            this.propertyManagerField.visible = Field.formOnly;
        }
        this.setupMemoTab();
    }

    /**
     * Memos are internal to the agent - shown to anyone acting for an agent, whether working in
     * the agency team (inAgency) or switched into an OMC (fromAgentTeamId set). An OMC director,
     * who has neither, never sees them.
     */
    private setupMemoTab() {
        const memoIdx = this.config.tabFields.indexOf(this.memoField);
        if (this.currentUser.inAgency || this.currentUser.fromAgentTeamId) {
            if (memoIdx < 0) {
                this.config.tabFields.push(this.memoField);
            }
        } else if (memoIdx >= 0) {
            this.config.tabFields.splice(memoIdx, 1);
        }
    }

    private openSectionEdit(editor: SectionEditor<ServiceRequest>) {
        if (this.sr) {
            editor.edit(this.sr.id).subscribe(updated => {
                if (updated) {
                    this.page.form.reloadContent(updated);
                }
            });
        }
    }

    editSummary() {
        this.openSectionEdit(new RequestSummaryEditor());
    }

    editDetails() {
        this.openSectionEdit(new RequestDetailsEditor());
    }

    editPeople() {
        this.openSectionEdit(new RequestPeopleEditor(this.sr?.teamId));
    }

    editWorkOrder() {
        this.openSectionEdit(new RequestWorkOrderEditor());
    }

    editApproval() {
        if (this.sr) {
            this.dialog.open(RequestAgendaDialogComponent, { data: this.sr }).afterClosed()
                .subscribe(() => this.reloadForm());
        }
    }

    /** An agency list spans many OMCs, each with its own Management channel - name the OMC too */
    private channelNameOf(sr: ServiceRequest): string {
        return this.currentUser?.inAgency ? sr.fullForumName : sr.forumName;
    }


    /** Every field the left and right rails place explicitly (see the template) */
    private railFields(): Field[] {
        return [
            this.statusPillField, this.actionStatusPillField, this.prioField, this.aiField,
            this.srTopicField, this.srClassField, this.billableField,
            this.forumField, this.approvedByField, this.approvedAtField,
            this.createdField, this.completedField,
            this.ownerReadField, this.lastUpdateField,
            this.refField, this.teamField, this.agentField, this.sourceField,
            this.requestTypeField, this.parentField,
            this.ownedByField, this.resolvedByField, this.reportedByField, this.personField,
            this.unitField, this.areaField, this.coreField,
            this.supplierField, this.propertyManagerField,
        ];
    }


    reloadForm() {
        this.dataSvc.getOne(this.page.form.focusItem.id, null).subscribe( sr => {
            if (sr) {
                this.page.form.reloadContent(sr);
            }
        })
    }

    // A comment goes to every follower, so offer the same follow-up we offer after a mail.
    promptForWaiting(): Observable<ServiceRequest> {
        return this.dataSvc.promptExpectResponse(this.page.form.focusItem as ServiceRequest);
    }

    /** Assembles the full field list; fields the rails place are class members, the rest are inline */
    getFields() {
        this.forumField = makeRequestChannelPillField(sr => this.channelNameOf(sr)).override({ formColumn: 4 });
        const forumIdField = FormPicklistComponent.make('Channel', 'forumId', 'fullForumName', {
            selfPopulate: true,
        }, {visible: Field.noShow})

        return [
            FormTextComponent.make('Keyword Search', 'keyword', { sendServer: false }).hide(),
            FieldMaker.id(),
            FieldMaker.rev(),
            FieldMaker.idHolder('jobId'),
            FieldMaker.idHolder('ballotId'),
            this.teamField,
            this.drilldownButton,
            this.refField,
            this.requestTypeField,
            this.forumField,
            this.prioField,
            this.statusPillField,
            this.actionStatusPillField,
            this.aiField,
            this.createdField,
            // editButton / closeButton - replaced by the Status pill's click; commented out
            // (definitions kept above) until the pill flow is fully tested.
            // this.editButton,
            // this.closeButton,
            ServiceRequest.getOpenMonthField(),
            this.openMonthPL,
            ServiceRequest.getOpenWeekField(),
            ServiceRequest.getCloseMonthField(),
            ServiceRequest.getCloseWeekField(),
            this.agentField,
            this.openField,
            this.plannedField,
            this.titleField,
            this.descriptionField,
            this.parentField,
            this.supplierField,
            this.unitField,
            this.personField,
            this.areaField,
            this.coreField,
            this.reportedByField,

            this.propertyManagerField,
            this.srTopicField,
            this.srClassField,
            this.ownedByField,
            this.billableField,

            this.ownerReadField,
            this.lastUpdateField,
            forumIdField,
            this.approvedAtField,
            this.approvedByField,
            ServiceRequest.getApprovedBySearchField(),

            this.completedField,
            this.resolvedByField,
            ServiceRequest.getResolvedBySearchField(),
            this.sourceField,

            this.templateField,
            this.weekField,
        ]
    }

    flattenData(dataFields: AbstractObject[], item: any, prefix = '') {
        for (const prop in item) {
            if (typeof item[prop] === 'object') {
                this.flattenData(dataFields, item[prop], prefix + prop + '.')
            } else {
                dataFields.push({ name: prefix + prop, value: item[prop] } as unknown as AbstractObject)
            }
        }
    }

    reloadFrom(sr: ServiceRequest) {
        this.page.form.reloadContent(sr);
    }

    /**
     * The header reads "<title> > <item title>", so the title and icon carry the type of the record
     * on screen. config.icon is header-only - the menu keeps the route's own icon. A request with no
     * type yet (or one that was not found) falls back to the list title and icon.
     */
    private setPageTitle(sr: ServiceRequest) {
        const type = ServiceRequest.requestTypes.find(t => t.id === sr?.requestType);
        this.config.title = type ? type.name : ServiceRequestPageComponent.listTitle;
        this.config.icon = type?.icon;
    }

    beforeList(data: ServiceRequest[]): ServiceRequest[] {
        this.config.title = ServiceRequestPageComponent.listTitle;
        this.config.icon = null;
        return data;
    }

    beforeEdit(sr: ServiceRequest): ServiceRequest {
        if (!sr) {
            sr = new ServiceRequest(); // Request was not found... don't want errors...
        }
        this.setPageTitle(sr);
        sr.dataFields = [];
        if (sr.data) {
            this.flattenData(sr.dataFields, sr.data)
        }
        this.chatField.disable = false;
        this.attachmentGrid.disable = false;
        this.actionGrid.disable = false;
        this.actionGrid.sendServer = false; //this.templateActionsField.sendServer = false;
        // actionGrid lives in the fieldSet, not tabFields, so nothing has handed it the form group
        // it needs before adding an action - do that here.
        this.actionGrid.relatedFormGroup = this.config.fieldSet.getFormGroup();
        //this.setEditTabFields();

        // Sort by date created descending
        sr.attachments = sr.attachments.sort((a, b) => a.createdAt < b.createdAt ? 1 : -1);
        sr.actions = sr.actions.sort((a, b) => a.plannedDate > b.plannedDate ? 1 : -1);

        // Child requests: still-active ones first, accepted (done with) sink to the bottom; newest first within each
        sr.childRequests = (sr.childRequests || []).sort((a, b) => {
            const aDone = a.srStatus === ServiceRequest.srAccepted.id ? 1 : 0;
            const bDone = b.srStatus === ServiceRequest.srAccepted.id ? 1 : 0;
            return aDone !== bDone ? aDone - bDone : (a.createdAt < b.createdAt ? 1 : -1);
        });

        this.actions.length = 0;
        if (this.config.allowEdit) {
            const addActions = new ActionHolder({name: 'Add', tipText: $localize`Add to this request`});
            addActions.add(new ImageUploadRequestAction())
            // Disabled - relied on generic PUT to apply templateId + actions[]
            // addActions.add(new AddTemplateAction(this.tmplService, this.dialog, this.cds, this.currentUser));
            addActions.add(new AddActionFormAction(this.actionGrid.upsertAction.bind(this.actionGrid)));
            if (this.currentUser.inAgency) {
                addActions.add(new AddMemoAction(this.memoField));
            }

            const mailActions = new ActionHolder({name: 'Mail', tipText: $localize`Send an email`, icon: 'send'});
            if (sr?.followers?.length > 0) {
                mailActions.add(new AddCommentAction(this.chatField, this.dataSvc));
            }            
            mailActions.add(new SendSupplierAction())
            sr.followers.forEach(f => mailActions.add(new SendEmailFormAction(f, sr)));

            addActions.add(new AddFollowerAction(this.chatField, mailActions, false, this.mailItemGrid));
            addActions.add(new AddFollowerAction(this.chatField, mailActions, true, this.mailItemGrid));

            this.actions.push(new ResolveRequestAction(this.dataSvc, this.dialog));
            this.actions.push(new ReOpenRequestAction());
            this.actions.push(new AcceptRequestAction());
            this.actions.push(new UnitDetailsAction(this.unitField, this.teamField));
            this.actions.push(new PersonDetailsAction(this.personField, this.teamField, this.unitField));

            this.actions.push(mailActions);
            this.actions.push(addActions);
        } else {
            if (this.currentUser?.currentRole?.globalRoleId === Role.ROLE.DIRECTOR.id) {
                this.actions.push(new ImageUploadRequestAction());
            }
        }

        this.actionGrid.setReadonly(!this.config.allowEdit);
        this.chatField.allowComments(this.config.allowEdit);

        this.askAssistant(sr);

        return sr;
    }

    /**
     * The assistant's answer, once per request per visit, on every case - closed ones too, so the
     * Assistant pill can say what the assistant did here. Whether to actually ask is the server's
     * decision - the OMC's AI Level setting, the agency's assistant contact, the request being open,
     * and never asking twice; an earlier answer comes back as it stands. See
     * RequestQualifyService::proposeForReview.
     *
     * Deliberately not restricted to agency users. An agent who has switched into the OMC is the
     * same person doing the same job, and the setting is what decides who gets this, not the team
     * the page happens to be standing in.
     */
    private askAssistant(sr: ServiceRequest) {
        if (!this.showAssistant(sr)) {
            return;
        }
        if (this.askedAbout !== sr.id) {
            this.askedAbout = sr.id;
            this.aiResult = null;
            this.dataSvc.getAiProposal(sr).pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(result => this.assistantAnswered(sr.id, result));
        }
        this.stampAssistant(sr);
    }

    /** Cases only, and only for someone who could act on the answer. */
    showAssistant(sr: ServiceRequest): boolean {
        return !!sr?.id && sr.requestType === ServiceRequest.typeCase.id && this.config.allowEdit;
    }

    /** Called in beforeEdit, before the form reads the record, so a reload keeps the pill's value. */
    private stampAssistant(sr: ServiceRequest) {
        sr.aiStatus = this.aiResult ? aiStatusOf(this.aiResult) : 'working';
        sr.aiMessage = this.aiResult?.message;
        sr.aiLog = this.aiResult?.log ?? undefined;
    }

    private assistantAnswered(srId: uuid, result: AiProposalResult) {
        if (this.sr?.id !== srId) {
            return;
        }
        this.aiResult = result;
        this.stampAssistant(this.sr);
        this.aiField.setValue(this.sr);
        if (result?.log?.outcome === AiLog.OUTCOME_PENDING) {
            this.reviewProposal(this.sr).subscribe(updated => {
                if (updated) {
                    this.page.form.reloadContent(updated);
                }
            });
        }
    }

    /** A request the reviewer changed is asked about again - cheaply, the server hands back the log. */
    reviewProposal(sr: ServiceRequest): Observable<ServiceRequest | null> {
        const data: AiProposalData = { sr, log: sr.aiLog, canSend: this.aiResult?.canSend ?? false };
        return this.dialog.open(AiProposalComponent, { data }).afterClosed().pipe(
            tap(updated => {
                if (updated) {
                    this.askedAbout = null;
                }
            }));
    }
}

